import { gql, useQuery } from "@apollo/client";
import React from "react";
import { GetAuditEventsQuery, AuditEventFilterInput } from "../../gql/graphql";
import { ShareparkDateTime } from "../../components/LocalTime";
import ShareParkTable, {
  usePaginationAndFilters,
} from "../../components/table/ShareParkTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";

const ALL_CHANGES = gql`
  query GetAllChanges(
    $where: AuditEventFilterInput
    $skip: Int
    $take: Int
    $orderBy: [AuditEventSortInput!]
  ) {
    auditEvents(where: $where, take: $take, skip: $skip, order: $orderBy) {
      items {
        id
        eventDate
        eventType
        userName
        entityType
        entityId
        changes
      }
      totalCount
    }
  }
`;

type AuditEvent = NonNullable<
  NonNullable<NonNullable<GetAuditEventsQuery["auditEvents"]>["items"]>[number]
>;

const columnHelper = createColumnHelper<AuditEvent>();

const columns = [
  columnHelper.accessor("eventDate", {
    enableColumnFilter: true,
    header: "When",
    cell: (info) => {
      return <ShareparkDateTime timestamp={info.getValue()} />;
    },
  }),
  columnHelper.accessor("eventType", {
    header: "Type",
  }),
  columnHelper.accessor("userName", {
    header: "User",
  }),
  columnHelper.accessor("entityType", {
    enableColumnFilter: true,
    enableSorting: false,
    header: "Entity",
    cell: (info) => {
      // we'll return a link to the entity type here
      const changes = JSON.parse(info.row.original.changes as string);

      return (
        <Link to={`/${changes.Table.toLowerCase()}/${changes.PrimaryKey.Id}`}>
          {changes.ColumnValues.Name
            ? `${changes.Name}: ${changes.ColumnValues.Name}`
            : changes.Name}
        </Link>
      );
    },
  }),
  //   columnHelper.accessor("entityId", {
  //     header: "Entity ID",
  //   }),
  columnHelper.accessor("changes", {
    enableColumnFilter: true,
    enableSorting: false,
    header: "Changes",
    cell: (info) => {
      if (info.row.original.eventType === "Update") {
        const changes = JSON.parse(info.getValue() as string);
        const valuesChanges = changes.Changes;

        const summary = valuesChanges.map((valueChange: any) => {
          let oldValue = valueChange.OriginalValue;
          let newValue = valueChange.NewValue;
          const fieldName = valueChange.ColumnName;

          if (typeof oldValue === "boolean") {
            oldValue = oldValue ? "Yes" : "No";
          }
          if (typeof newValue === "boolean") {
            newValue = newValue ? "Yes" : "No";
          }

          // Check if the values are integers and format them if necessary
          if (typeof oldValue === "number" && Number.isInteger(oldValue)) {
            oldValue = oldValue.toLocaleString(); // Adds commas for thousands
          }
          if (typeof newValue === "number" && Number.isInteger(newValue)) {
            newValue = newValue.toLocaleString(); // Adds commas for thousands
          }

          if (oldValue === null || oldValue === undefined) {
            oldValue = "{empty}";
          }

          if (newValue === null || newValue === undefined) {
            newValue = "{empty}";
          }

          return (
            <div>
              <span className="text-muted">{fieldName}</span>:{" "}
              <span
                className="text-danger text-decoration-line-through"
                style={{
                  display: "inline-block",
                  maxWidth: "20ch", // Limit to 10 characters
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  verticalAlign: "middle",
                }}
              >
                {oldValue}
              </span>{" "}
              {"->"}{" "}
              <span
                className="text-success"
                style={{
                  display: "inline-block",
                  maxWidth: "20ch", // Limit to 10 characters
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  verticalAlign: "middle",
                }}
              >
                {newValue}
              </span>
            </div>
          );
        });

        return <pre>{summary}</pre>;
      }

      if (info.row.original.eventType === "Insert") {
        const changes = JSON.parse(info.getValue() as string);
        const colValues = changes.ColumnValues;

        const summary = Object.entries(colValues).map(([key, value]) => {
          let displayValue = value;
          if (typeof displayValue === "boolean") {
            displayValue = displayValue ? "Yes" : "No";
          }
          if (
            typeof displayValue === "number" &&
            Number.isInteger(displayValue)
          ) {
            displayValue = displayValue.toLocaleString(); // Adds commas for thousands
          }

          if (
            displayValue === null ||
            displayValue === undefined ||
            displayValue === ""
          ) {
            return null;
            // displayValue = "{empty}";
          }

          return (
            <div key={key}>
              <span className="text-muted">{key}</span>:{" "}
              <span className="text-success">{displayValue as string}</span>
            </div>
          );
        });

        return <pre>{summary}</pre>;
      }

      return <pre>{info.getValue()}</pre>;
    },
  }),
];

const Changes: React.FC = () => {
  const { pagination, setPagination, columnFilters, setColumnFilters } =
    usePaginationAndFilters();

  // Add sorting state
  const [sorting, setSorting] = React.useState([
    {
      id: "eventDate", // Default sorting column
      desc: true, // Default sorting direction (descending)
    },
  ]);

  const where = React.useMemo(() => {
    const where: AuditEventFilterInput = {};

    for (const filter of columnFilters) {
      switch (filter.id) {
        case "eventDate":
          if (filter.value != null) {
            where.eventDate = { eq: String(filter.value) };
          }
          break;
        case "eventType":
          if (filter.value != null) {
            where.eventType = { eq: String(filter.value) };
          }
          break;
        case "userName":
          if (filter.value != null) {
            where.userName = { contains: String(filter.value) };
          }
          break;
        case "entityType":
          if (filter.value != null) {
            where.entityType = { contains: String(filter.value) };
          }
          break;
        case "entityId":
          if (filter.value != null) {
            where.entityId = { contains: String(filter.value) };
          }
          break;
      }
    }

    return where;
  }, [columnFilters]);

  const orderBy = React.useMemo(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      return {
        [sort.id]: sort.desc ? "DESC" : "ASC",
      };
    }
    return undefined;
  }, [sorting]);

  const { loading, error, data } = useQuery<GetAuditEventsQuery>(ALL_CHANGES, {
    variables: {
      take: pagination.pageSize,
      skip: pagination.pageSize * pagination.pageIndex,
      where: where,
      orderBy: orderBy ? [orderBy] : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Changes</h1>
      </div>

      {loading && <p>Loading...</p>}

      {error && <p>Error: {error.message}</p>}

      {data && data.auditEvents && (
        <ShareParkTable
          columns={columns as ColumnDef<AuditEvent>[]}
          data={data.auditEvents ?? { items: [], totalCount: 0 }}
          pagination={pagination}
          setPagination={setPagination}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          sorting={sorting} // Pass sorting state
          setSorting={setSorting} // Pass sorting setter
        />
      )}
    </>
  );
};

export default Changes;
